import { arrayMove } from '@dnd-kit/sortable';
import type {
  LineupSlot,
  LineupVersus,
  ScholarshipLineups,
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { isPitcherPosition } from './rosterPalette';

/**
 * The lineup rules, kept pure so they are testable without a board: a batting order is 1-9 with no
 * gaps once this client has touched it (the API allows gaps; this client never writes one), and
 * the bench is everyone who could hit and is not in the order.
 */

export const LINEUP_MAX = 9;

export const LINEUP_SIDES: readonly LineupVersus[] = ['lhp', 'rhp'];

export const lineupKey = (side: LineupVersus): keyof ScholarshipLineups =>
  side === 'lhp' ? 'vsLhp' : 'vsRhp';

export const LINEUP_TITLES: Record<LineupVersus, string> = {
  lhp: 'vs LHP',
  rhp: 'vs RHP',
};

/** Sorted by batting order and renumbered 1..n, so a gap the server allowed closes on next save. */
export const normalizeSlots = (slots: readonly LineupSlot[]): LineupSlot[] =>
  [...slots]
    .sort((a, b) => a.battingOrder - b.battingOrder)
    .map((slot, index) => ({ ...slot, battingOrder: index + 1 }));

/** Drops slots whose player is no longer on the board, so a save never names a stale row. */
export const knownSlots = (
  slots: readonly LineupSlot[],
  playersById: ReadonlyMap<string, ScholarshipPlayer>,
): LineupSlot[] => slots.filter((slot) => playersById.has(slot.playerId));

/**
 * Whether a player can take a spot in the order: anyone with a position that is not on the mound.
 * A two-way player (primary RHP, secondary SS) can hit. A player with no position at all can hit
 * too. Only a pitcher with no field position stays out.
 */
export const canHit = (player: Pick<ScholarshipPlayer, 'position' | 'secondaryPosition'>): boolean =>
  !isPitcherPosition(player.position) ||
  (player.secondaryPosition != null && !isPitcherPosition(player.secondaryPosition));

/**
 * Where a player fields when the lineup names no position: their primary, unless that is a hand
 * and they have a field position as their secondary (a two-way RHP/SS plays short).
 */
export const defaultFieldPosition = (
  player: Pick<ScholarshipPlayer, 'position' | 'secondaryPosition'>,
): ScholarshipPlayerPosition | null =>
  isPitcherPosition(player.position) &&
  player.secondaryPosition != null &&
  !isPitcherPosition(player.secondaryPosition)
    ? player.secondaryPosition
    : player.position;

/** Everyone on the board who can hit and is not in this lineup, in board order. */
export const benchFor = (
  players: readonly ScholarshipPlayer[],
  slots: readonly LineupSlot[],
): ScholarshipPlayer[] => {
  const inLineup = new Set(slots.map((s) => s.playerId));
  return players.filter((p) => canHit(p) && !inLineup.has(p.id));
};

/** Bats last. Unchanged when the order is full or the player is already in it. */
export const addToLineup = (slots: readonly LineupSlot[], playerId: string): LineupSlot[] => {
  const ordered = normalizeSlots(slots);
  if (ordered.length >= LINEUP_MAX || ordered.some((s) => s.playerId === playerId)) return ordered;
  return [...ordered, { battingOrder: ordered.length + 1, playerId, fieldPosition: null }];
};

/** Back to the bench; everyone below moves up a spot. */
export const removeFromLineup = (slots: readonly LineupSlot[], playerId: string): LineupSlot[] =>
  normalizeSlots(slots.filter((s) => s.playerId !== playerId));

/** The player dragged (`activeId`) takes the spot of the one dropped on (`overId`). */
export const moveInLineup = (
  slots: readonly LineupSlot[],
  activeId: string,
  overId: string,
): LineupSlot[] => {
  const ordered = normalizeSlots(slots);
  const from = ordered.findIndex((s) => s.playerId === activeId);
  const to = ordered.findIndex((s) => s.playerId === overId);
  if (from < 0 || to < 0 || from === to) return ordered;
  return normalizeSlots(
    arrayMove(ordered, from, to).map((slot, index) => ({ ...slot, battingOrder: index + 1 })),
  );
};

/**
 * Where the player fields in this lineup. Their default position (`defaultFieldPosition`: the
 * primary, or a two-way pitcher's field secondary) is stored as null, so a later change to the
 * player's position carries into the lineup by itself.
 */
export const setFieldPosition = (
  slots: readonly LineupSlot[],
  playerId: string,
  position: ScholarshipPlayerPosition | null,
  fallback: ScholarshipPlayerPosition | null,
): LineupSlot[] =>
  normalizeSlots(slots).map((slot) =>
    slot.playerId === playerId
      ? { ...slot, fieldPosition: position === fallback ? null : position }
      : slot,
  );
